


















































































import { defineComponent, computed, ref, Ref, watch, onMounted, onUnmounted } from '@vue/composition-api';
import { CircleSpinner, AlertTriangleIcon } from '@nimiq/vue-components';
import TransactionListItem from '@/components/TransactionListItem.vue';
import TestnetFaucet from './TestnetFaucet.vue';
import CrossCloseButton from './CrossCloseButton.vue';
import { useAddressStore } from '../stores/Address';
import { useNetworkStore } from '../stores/Network';
import { ENV_MAIN } from '../lib/Constants';
import { isProxyData, ProxyType, ProxyTransactionDirection } from '../lib/ProxyDetection';
import { createCashlink } from '../hub';
import { useConfig } from '../composables/useConfig';
import LoadingList, { LoadingListType } from './LoadingList.vue';
import StakingRewardsListItem from './StakingRewardsListItem.vue';
import { useTransactionList } from '../composables/useTransactionList';

export default defineComponent({
    props: {
        searchString: {
            type: String,
            default: '',
        },
        showUnclaimedCashlinkList: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        const { activeAddress, activeAddressInfo, transactionsForActiveAddress } = useAddressStore();
        const { isFetchingTxHistory, fetchedAddresses } = useNetworkStore();
        const { config } = useConfig();

        // Amount of pixel to add to edges of the scrolling visible area to start rendering items further away
        const scrollerBuffer = 300;

        const txCount = computed(() => transactionsForActiveAddress.value.length);

        const unclaimedCashlinkTxs = computed(() => transactionsForActiveAddress.value.filter(
            (tx) => tx.sender === activeAddress.value && !tx.relatedTransactionHash
                && isProxyData(tx.data.raw, ProxyType.CASHLINK, ProxyTransactionDirection.FUND),
        ));

        // Count unclaimed cashlinks
        watch(() => {
            const count = unclaimedCashlinkTxs.value.length;
            context.emit('unclaimed-cashlink-count', count);
        });

        const { transactions, itemSize } = useTransactionList({
            searchString: computed(() => props.searchString),
            scrollerBuffer,
        });

        const root: Ref<null | HTMLElement> = ref(null);

        // Does not need to be reactive, as the environment doesn't change during runtime.
        const isMainnet = config.environment === ENV_MAIN;

        function onCreateCashlink() {
            createCashlink(activeAddress.value!, activeAddressInfo.value!.balance || undefined);
        }

        // Scroll to top when
        // - Active address changes
        // - Unclaimed Cashlinks list is opened
        const scroller = ref<{
            scrollToPosition(position: number, smooth?: boolean): void,
            $el: HTMLDivElement,
                } | null>(null);

        watch(activeAddress, () => {
            if (scroller.value) {
                scroller.value.scrollToPosition(0, false); // No smooth scrolling on address change
            }
        });

        watch(() => props.showUnclaimedCashlinkList, (show) => {
            if (show && scroller.value) {
                scroller.value.scrollToPosition(0, true);
            }
        });

        function onScroll() {
            context.emit('scroll');
        }

        // @scroll / @scroll.native doesn't seem to work, so using standard event system
        onMounted(() => {
            if (!scroller.value) return;
            scroller.value.$el.addEventListener('scroll', onScroll);
        });

        onUnmounted(() => {
            if (!scroller.value) return;
            scroller.value.$el.removeEventListener('scroll', onScroll);
        });

        const hasFetchedAddress = computed(
            () => !!activeAddress.value && fetchedAddresses.value.includes(activeAddress.value),
        );

        function getMonthFromReward(item: any) {
            if (typeof item.transactionHash === 'string' && item.transactionHash.startsWith('monthly-reward-')) {
                const parts = item.transactionHash.split('-');
                return `${parts[2]}-${parts[3]}`;
            }
            return '';
        }

        return {
            LoadingListType,
            activeAddress,
            scrollerBuffer,
            itemSize,
            txCount,
            transactions,
            root,
            isFetchingTxHistory,
            hasFetchedAddress,
            isMainnet,
            unclaimedCashlinkTxs,
            onCreateCashlink,
            scroller,
            getMonthFromReward,
        };
    },
    components: {
        TransactionListItem,
        TestnetFaucet,
        CrossCloseButton,
        CircleSpinner,
        AlertTriangleIcon,
        LoadingList,
        StakingRewardsListItem,
    },
});
